<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { config } from "@/common/settings/config";

import { userList, userAdd, userUpdate, userDel } from "@/common/api/rest/user";

import type { UserType, UserRegisterType } from "@/common/types/user";

import { PanelTheme } from "@/common/themes";
import { FormUser } from "@/components";
import {
  breadcrumbSimple,
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t, tm } = useI18n();

const tableData = ref<UserType[]>([]);
const selected = ref<UserType | null>(null);
const payload = ref<UserRegisterType | null>(null);

const formUserRef = ref<InstanceType<typeof FormUser> | null>(null);

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);
const isEdit = ref(false);

const userTypes = computed(() => tm("user.types") as Record<string, string>);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "users",
    label: t("common.labels.users"),
    url: "/register/user",
  },
]);

const tableHeader = computed(() => [
  { key: "id", label: "" },
  { key: "name", label: t("common.labels.name") },
  { key: "type", label: t("common.labels.type") },
  { key: "status", label: t("common.labels.status") },
  { key: "email", label: t("common.labels.email") },
]);

const optionStatus = computed(() => ({
  ACTIVE: t("common.labels.active"),
  INACTIVE: t("common.labels.inactive"),
}));

function fetchData() {
  userList({
    limit: limit.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;

        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changePage(value: number) {
  page.value = value;
}

function searchItem(value: string) {
  search.value = value;

  fetchData();
}

function addItem() {
  showModalAdd.value = true;
  isEdit.value = false;
}

function addItemConfirm() {
  formUserRef.value?.valid();

  if (!payload.value || !formUserRef.value?.isValid) return;

  store.commit("SET_LOADING", true);

  userAdd(payload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        submitCancel();
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editItem(value: UserType) {
  selected.value = value;
  showModalAdd.value = true;
  isEdit.value = true;
}

function editItemConfirm() {
  formUserRef.value?.valid();

  if (!payload.value || !formUserRef.value?.isValid) {
    return;
  }

  store.commit("SET_LOADING", true);

  userUpdate(selected.value.user_id, payload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        submitCancel();
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function removeItem(data: UserType) {
  if (!data.deletable) {
    toast({ message: t("common.texts.not_deletable"), type: "error" });
    return;
  }

  selected.value = data;
  showModalDel.value = true;
}

function removeItemCancel() {
  selected.value = null;
  showModalDel.value = false;
}

function removeItemConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);

  userDel(selected.value.user_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        removeItemCancel();
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submitCancel() {
  selected.value = null;
  payload.value = null;
  payloadValid.value = false;
  showModalAdd.value = false;
}

function submitPayload() {
  if (isEdit.value) {
    editItemConfirm();
  } else {
    addItemConfirm();
  }
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="users"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <svg-icon icon="group" size="2rem" />
        {{ t("common.labels.users") }}
      </div>
      <div class="tab-content">
        <base-table
          :header="tableHeader"
          :data="tableData"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :del="true"
          :edit="true"
          @change:page="changePage($event)"
          @searching="searchItem($event)"
          @btn:add="addItem()"
          @btn:edit="editItem($event)"
          @btn:del="removeItem($event)"
        >
          <template #status="{ data }">
            {{ optionStatus[data.status]?.toUpperCase() }}
          </template>

          <template #name="{ data }">
            {{ data.first_name }} {{ data.last_name ?? "" }}
          </template>

          <template #type="{ data }">
            {{ userTypes[data.type] }}
          </template>
        </base-table>
      </div>
    </section>
  </article>

  <base-modal
    :show="showModalAdd"
    :title="t('user.modals.titles.user_add').toUpperCase()"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.save')"
    :button-close="t('common.labels.cancel')"
    @close="submitCancel()"
    @confirm="submitPayload()"
  >
    <template #content>
      <form-user
        ref="formUserRef"
        :data="selected"
        :is-edit="isEdit"
        @update-data="payload = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showModalDel"
    :title="t('user.modals.titles.user_remove').toUpperCase()"
    :message="t('user.modals.messages.user_remove', { name: selected?.name })"
    @cancel="removeItemCancel()"
    @confirm="removeItemConfirm()"
  />
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: inline-flex;
      gap: 0.5rem;
      font-size: 1.2rem;
      align-items: center;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);
      text-transform: uppercase;
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      padding-inline: 1.5rem;
    }
  }
}
</style>
